import { useState, useCallback, useEffect, useRef } from 'react';
import { Header, Chat, ChatHistorySidebar, LoginPage, AiAssistantInstructionPopup } from './components';
import { useAuth } from './hooks/useAuth';
import { ENABLE_AI_ASSISTANT_INSTRUCTION } from './utils/constants';
import { useChatList } from './hooks/useChatList';
import { cn } from './utils';
import './App.css';

function App() {
  const { user, isAuthenticated, isLoading: authLoading, isLoggingIn, error: authError, login, logout, clearError: clearAuthError } = useAuth();
  const [chatKey, setChatKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const healthCheckExecuted = useRef(false);
  const { items: chatHistory, isLoading: chatListLoading, error: chatListError } = useChatList(sidebarOpen, isAuthenticated);

  // Проверяем здоровье API при загрузке приложения (только один раз)
  useEffect(() => {
    if (healthCheckExecuted.current) return;

    const checkApiHealth = async () => {
      try {
        healthCheckExecuted.current = true;
      } catch (error) {
        console.error('API Health Check Failed:', error);
      }
    };

    checkApiHealth();
  }, []);

  const handleNewChat = useCallback(() => {
    localStorage.removeItem('chat_thread_id');
    setSelectedChatId(null);
    setChatKey(prev => prev + 1);
  }, []);

  const handleSelectChat = useCallback((id: string) => {
    localStorage.setItem('chat_thread_id', id);
    setSelectedChatId(id);
    setChatKey(prev => prev + 1);
  }, []);

  // Пока грузится сессия — не показываем ни логин, ни чат
  if (authLoading) {
    return (
      <div className="app app--loading">
        <div className="app__loading-spinner" />
        <p className="app__loading-text">Загрузка...</p>
      </div>
    );
  }

  // Не авторизован — экран входа (регистрации нет)
  if (!isAuthenticated) {
    return (
      <LoginPage
        onLogin={login}
        error={authError}
        onClearError={clearAuthError}
        isLoading={isLoggingIn}
      />
    );
  }

  return (
    <div className="app">
      <ChatHistorySidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        items={chatHistory}
        selectedId={selectedChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        isLoading={chatListLoading}
        error={chatListError}
      />
      <div className={cn('app__main', sidebarOpen && 'app__main--sidebar-open')}>
        <Header
          user={user}
          onNewChat={handleNewChat}
          onOpenSidebar={() => setSidebarOpen(true)}
          onLogout={logout}
        />
        <div className="app__chat-with-help">
          <Chat key={chatKey} />
          {user && ENABLE_AI_ASSISTANT_INSTRUCTION && (
            <AiAssistantInstructionPopup userLogin={user.login} />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;